export type TestModule = 'LISTENING' | 'READING' | 'WRITING';
export type TestVariant = 'ACADEMIC' | 'GENERAL';
export type TestStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type InputMode =
  | 'GAP' | 'TEXT' | 'CHOICE_ONE' | 'CHOICE_MANY' | 'BANK' | 'FIXED'
  /** One letter per row, marked in a grid whose columns are the bank letters. */
  | 'GRID'
  /** A card dragged out of a shared bank into a numbered blank. */
  | 'DRAG'
  /** An extended written response, judged by a person rather than a key. */
  | 'ESSAY';

export interface TestSummary {
  id: string;
  title: string;
  /** The whole test this paper is one module of, where it belongs to one. */
  setName?: string | null;
  /**
   * Which shelf it sits on. CURRENT is what the centre teaches from now, PAST
   * is what it keeps — both can be set on a class, unlike an archived paper.
   */
  shelf?: 'CURRENT' | 'PAST';
  /**
   * Which half of the library it is filed under: the papers recalled from
   * real sittings, or the rest. A kind, not a lifecycle — both halves are
   * taught from, and both appear on either shelf.
   */
  collection?: 'ACTUAL' | 'GENERAL';
  module: TestModule;
  variant: TestVariant;
  status: TestStatus;
  questionCount: number;
  /**
   * How many candidates have sat it. Anything above zero means it can only be
   * archived: deleting it would take their results with it, and the server
   * refuses. Older responses do not carry it, so it is optional.
   */
  sittings?: number;
  /**
   * Whether this centre may change the paper, as against merely teach from
   * it. A paper lent to the platform by another centre is read-only here.
   */
  owned?: boolean;
  /** Whether every centre on the platform can teach from it. */
  shared?: boolean;
  /**
   * Whether it belongs to the platform rather than to any one centre. Every
   * centre teaches from it; none of them, including the one whose library it
   * sits in, may change it.
   */
  platform?: boolean;
  /** The centre it was lent by, where a centre lent it. */
  sharedFrom?: string | null;
  hasAudio: boolean;
  createdAt: string;
  publishedAt: string | null;
}

export interface Diagnostic {
  line: number;
  message: string;
  severity: 'error' | 'warning';
}

export interface ImportReport {
  sourceFilename: string;
  notes: string[];
  diagnostics: Diagnostic[];
  expectedAudioFilename: string | null;
  expectedImageFilenames: string[];
}

export interface Readiness {
  ready: boolean;
  questionCount: number;
  problems: string[];
  /** Worth knowing before a sitting, but not a reason to hold the paper back. */
  warnings?: string[];
  missingAssets: { kind: 'AUDIO' | 'IMAGE'; filename: string }[];
}

export interface BankOption {
  letter: string;
  text: string;
}

export interface ReviewQuestion {
  id: string;
  number: number;
  /**
   * Both ends of a question worth several marks — "Choose THREE letters" is
   * one question numbered 18–20. Null on a question worth one mark.
   */
  spanFrom?: number | null;
  spanTo?: number | null;
  promptHtml: string | null;
  options: BankOption[] | null;
  /**
   * Where this gap sits on the group's artwork, as a percentage across and
   * down. Null on everything except a labelling paper that places its own.
   */
  at?: { x: number; y: number } | null;
  answer: { raw: string; accepted: string[] };
}

export interface ReviewGroup {
  id: string;
  type: string;
  typeLabel: string;
  /**
   * What this group could be relabelled as, if the importer read it wrongly.
   * Worked out by the server, so the screen offers exactly what will be
   * accepted rather than a list it has to keep in step by hand.
   */
  alternatives?: { type: string; label: string }[];
  inputMode: InputMode;
  /** Writing only: the rubric's minimum length, and the suggested minutes. */
  minWords?: number | null;
  recommendedMinutes?: number | null;
  fixedOptions: string[] | null;
  fromQ: number;
  toQ: number;
  instructions: string | null;
  bodyHtml: string | null;
  optionsBank: BankOption[] | null;
  maxWords: number | null;
  imageUrl: string | null;
  imageFilename: string | null;
  questions: ReviewQuestion[];
}

export interface ReviewPart {
  id: string;
  index: number;
  /** This part's own recording, where the paper has one per part. */
  audio: { url: string; filename: string; durationMs: number | null } | null;
  /** What the imported document said this part's recording is called. */
  audioFilename: string | null;
  instructions: string | null;
  passageTitle: string | null;
  passageHtml: string | null;
  /** The same passage as the text it was made from, so it can be edited. */
  passageText?: string;
  groups: ReviewGroup[];
}

export interface TestDetail {
  test: {
    id: string;
    title: string;
    /** The whole test this paper is one module of, where it belongs to one. */
    setName?: string | null;
    module: TestModule;
    variant: TestVariant;
    status: TestStatus;
    /** Whether this centre may change it, or only teach from it. */
    owned?: boolean;
    /** Whether every centre on the platform can teach from it. */
    shared?: boolean;
    /** Whether it belongs to the platform rather than to any one centre. */
    platform?: boolean;
    durationMs: number | null;
    /** In order. One entry for a continuous recording, one per part otherwise. */
    audio: { url: string; filename: string; durationMs: number | null }[];
    createdAt: string;
    publishedAt: string | null;
    importReport: ImportReport | null;
    readiness: Readiness;
  };
  parts: ReviewPart[];
}

export interface ImportOutcome {
  testId: string;
  title: string;
  diagnostics: Diagnostic[];
  notes: string[];
  expectedAudioFilename: string | null;
  expectedImageFilenames: string[];
}

/** "1h 0m" / "3m 30s" — durations here are exam lengths, never sub-second. */
export function formatDuration(ms: number | null): string {
  if (!ms) return '—';
  const total = Math.round(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/**
 * The same paper with a freshly-asked readiness verdict.
 *
 * Correcting an answer key on the review screen saves one question, and for a
 * while that was all it did: the panel listing what still stands between the
 * paper and its students had been worked out when the page loaded and was
 * never asked again. A teacher who fixed the last thing on the list watched it
 * go on saying the thing was broken, with Publish still greyed out — the fix
 * had gone in, the screen just never re-read it.
 *
 * Only the verdict is replaced. The parts keep their identity so the rows a
 * teacher is working down are not rebuilt underneath them, which would take
 * the cursor out of the box they are typing in.
 */
export function withReadiness(detail: TestDetail, readiness: Readiness): TestDetail {
  return { ...detail, test: { ...detail.test, readiness }, parts: detail.parts };
}
